'use client';

import React, { useState } from 'react';
import { Search, ChevronUp, ChevronDown, TrendingUp } from 'lucide-react';
import type { StaffEarning, PayPeriod } from '../page';

interface EarningsSummaryTableProps {
  earnings: StaffEarning[];
  period: PayPeriod;
}

type SortKey = keyof StaffEarning;
type SortDir = 'asc' | 'desc';

const roleBadge: Record<string, string> = {
  RN: 'badge-rn',
  CNA: 'badge-cna',
  LPN: 'badge-lpn',
  NP: 'badge-np',
  MD: 'badge-md',
};

export default function EarningsSummaryTable({ earnings, period }: EarningsSummaryTableProps) {
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('grossPay');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [roleFilter, setRoleFilter] = useState('all');

  const roles = ['all', ...Array.from(new Set(earnings.map((e) => e.role)))];

  const filtered = earnings
    .filter((e) => {
      const matchSearch =
        e.staffName.toLowerCase().includes(search.toLowerCase()) ||
        e.department.toLowerCase().includes(search.toLowerCase());
      const matchRole = roleFilter === 'all' || e.role === roleFilter;
      return matchSearch && matchRole;
    })
    .sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDir === 'asc' ? av - bv : bv - av;
      }
      return sortDir === 'asc'
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const SortIcon = ({ col }: { col: SortKey }) => {
    if (sortKey !== col) return <ChevronUp size={12} className="text-muted-foreground/40" />;
    return sortDir === 'asc' ? (
      <ChevronUp size={12} className="text-primary" />
    ) : (
      <ChevronDown size={12} className="text-primary" />
    );
  };

  const totals = filtered.reduce(
    (acc, e) => ({
      regularHours: acc.regularHours + e.regularHours,
      overtimeHours: acc.overtimeHours + e.overtimeHours,
      regularPay: acc.regularPay + e.regularPay,
      overtimePay: acc.overtimePay + e.overtimePay,
      grossPay: acc.grossPay + e.grossPay,
      deductions: acc.deductions + e.deductions,
      netPay: acc.netPay + e.netPay,
    }),
    { regularHours: 0, overtimeHours: 0, regularPay: 0, overtimePay: 0, grossPay: 0, deductions: 0, netPay: 0 }
  );

  const fmt = (n: number) => `$${n.toLocaleString()}`;

  const cols: { key: SortKey; label: string; align?: string }[] = [
    { key: 'staffName', label: 'Staff Member' },
    { key: 'role', label: 'Role' },
    { key: 'department', label: 'Department' },
    { key: 'regularHours', label: 'Reg Hrs', align: 'right' },
    { key: 'overtimeHours', label: 'OT Hrs', align: 'right' },
    { key: 'regularPay', label: 'Reg Pay', align: 'right' },
    { key: 'overtimePay', label: 'OT Pay', align: 'right' },
    { key: 'grossPay', label: 'Gross Pay', align: 'right' },
    { key: 'deductions', label: 'Deductions', align: 'right' },
    { key: 'netPay', label: 'Net Pay', align: 'right' },
  ];

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      {/* Table toolbar */}
      <div className="flex items-center gap-3 p-4 border-b border-border flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search staff or department…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-2 text-sm bg-muted border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>
        <div className="flex gap-1.5">
          {roles.map((r) => (
            <button
              key={r}
              onClick={() => setRoleFilter(r)}
              className={`px-3 py-1.5 rounded-lg text-xs font-500 transition-colors capitalize ${
                roleFilter === r
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              {r}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground ml-auto">
          <TrendingUp size={13} />
          <span>{filtered.length} staff members</span>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/50">
              {cols.map((col) => (
                <th
                  key={col.key}
                  onClick={() => handleSort(col.key)}
                  className={`px-4 py-3 text-xs font-600 text-muted-foreground uppercase tracking-wide cursor-pointer select-none whitespace-nowrap ${
                    col.align === 'right' ? 'text-right' : 'text-left'
                  }`}
                >
                  <span className="inline-flex items-center gap-1">
                    {col.label}
                    <SortIcon col={col.key} />
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((e) => (
              <tr key={e.id} className="border-b border-border row-hover transition-colors">
                <td className="px-4 py-3 font-500 text-foreground whitespace-nowrap">{e.staffName}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-600 ${roleBadge[e.role] ?? 'bg-muted text-muted-foreground'}`}>
                    {e.role}
                  </span>
                </td>
                <td className="px-4 py-3 text-muted-foreground">{e.department}</td>
                <td className="px-4 py-3 text-right tabular-nums">{e.regularHours}</td>
                <td className="px-4 py-3 text-right tabular-nums">
                  <span className={e.overtimeHours > 0 ? 'text-warning font-600' : 'text-muted-foreground'}>
                    {e.overtimeHours}
                  </span>
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-muted-foreground">{fmt(e.regularPay)}</td>
                <td className="px-4 py-3 text-right tabular-nums">
                  <span className={e.overtimePay > 0 ? 'text-warning font-600' : 'text-muted-foreground'}>
                    {fmt(e.overtimePay)}
                  </span>
                </td>
                <td className="px-4 py-3 text-right tabular-nums font-600 text-foreground">{fmt(e.grossPay)}</td>
                <td className="px-4 py-3 text-right tabular-nums text-danger">{fmt(e.deductions)}</td>
                <td className="px-4 py-3 text-right tabular-nums font-700 text-positive">{fmt(e.netPay)}</td>
              </tr>
            ))}
          </tbody>
          {/* Totals row */}
          <tfoot>
            <tr className="bg-muted/60 border-t-2 border-border">
              <td className="px-4 py-3 font-700 text-foreground" colSpan={3}>
                Totals ({filtered.length} staff)
              </td>
              <td className="px-4 py-3 text-right tabular-nums font-700">{totals.regularHours}</td>
              <td className="px-4 py-3 text-right tabular-nums font-700 text-warning">{totals.overtimeHours}</td>
              <td className="px-4 py-3 text-right tabular-nums font-700">{fmt(totals.regularPay)}</td>
              <td className="px-4 py-3 text-right tabular-nums font-700 text-warning">{fmt(totals.overtimePay)}</td>
              <td className="px-4 py-3 text-right tabular-nums font-700 text-foreground">{fmt(totals.grossPay)}</td>
              <td className="px-4 py-3 text-right tabular-nums font-700 text-danger">{fmt(totals.deductions)}</td>
              <td className="px-4 py-3 text-right tabular-nums font-700 text-positive">{fmt(totals.netPay)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
